import axios from "axios";

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { createOrder } from "../../actions/orderActions";
import { fetchPriceSummary } from "../../actions/priceActions";
import { useCart } from "../../components/CartContext";
import {
  useRestaurantSession,
} from "../../components/RestaurantSessionProvider";
import {
  isSessionExpiredError,
  getSessionExpiredMessage,
} from "../../utils/sessionErrorHandler";

import "./css/OrderScreen.css";

function OrderScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { restaurantId, tableId } = useParams();

  // DINING SESSION
  const { sessionToken } = useRestaurantSession();

  // CART
  const { cart, cartCount, clearCart } = useCart();

  // ORDER REDUX
  const {
    loading,
    error,
    errorCode,
  } = useSelector((state) => state.order || {});

  // SERVER PRICE SUMMARY
  const {
    loading: priceLoading,
    summary: priceSummary,
    error: priceError,
  } = useSelector((state) => state.price || {});

  // FORM STATE
  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [paymentOptions, setPaymentOptions] = useState(null);
  useEffect(() => {
    let live = true;
    setPaymentOptions(null);
    axios.get(`/api/restaurants/${encodeURIComponent(restaurantId)}/payment-options/`)
      .then(({ data }) => {
        if (!live) return;
        const cash = data.allow_cash_payment === true;
        const online = data.allow_online_payment === true;
        setPaymentOptions({ allow_cash_payment: cash, allow_online_payment: online });
        setPaymentMethod((current) =>
          current === "ONLINE" && online ? "ONLINE" :
          current === "CASH" && cash ? "CASH" :
          cash ? "CASH" : online ? "ONLINE" : ""
        );
      })
      .catch(() => {
        if (live) setPaymentOptions({ allow_cash_payment: false, allow_online_payment: false });
      });
    return () => { live = false; };
  }, [restaurantId]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const basePath = `/${restaurantId}/table/${tableId}`;

  // FETCH SERVER-CALCULATED PRICES
  useEffect(() => {
    if (cart.length === 0) {
      return;
    }

    const items = cart.map((item) => ({
      menu_item_id: item.id,
      quantity: item.quantity,
    }));

    dispatch(fetchPriceSummary(restaurantId, items));
  }, [dispatch, restaurantId, cart]);

  const finalPriceReady =
    !priceLoading &&
    !priceError &&
    !!priceSummary &&
    Number.isFinite(Number(priceSummary.total));

  // CREATE ORDER
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!paymentOptions || !paymentOptions[`allow_${paymentMethod.toLowerCase()}_payment`]) {
      alert("Selected payment method is unavailable. Please choose an enabled method.");
      return;
    }
    if (submitting || loading) {
      return;
    }

    if (cart.length === 0) {
      alert("Your cart is empty.");
      navigate(basePath);
      return;
    }

    if (!sessionToken) {
      alert(
        "Dining session is not ready. Please scan the table QR code again."
      );
      return;
    }

    if (!finalPriceReady) {
      alert(
        "Final price is not ready. Please wait or try again."
      );
      return;
    }

    const orderData = {
      items: cart.map((item) => ({
        menu_item_id: item.id,
        quantity: item.quantity,
      })),
      session_token: sessionToken,
      payment_method: paymentMethod,
      table_id: Number(tableId),
      notes: notes,
    };

    setSubmitting(true);

    try {
      const createdOrder = await dispatch(
        createOrder(restaurantId, orderData)
      );

      if (!createdOrder?.id) {
        throw new Error(
          "Order was not confirmed by the server."
        );
      }

      // CASH ORDER
      if (paymentMethod === "CASH") {
        clearCart();

        navigate(`${basePath}/success`, {
          replace: true,
        });

        return;
      }

      // ONLINE ORDER
      // Do not clear the cart until payment is verified.
      navigate(`${basePath}/payment`);
    } catch (orderError) {
      if (isSessionExpiredError(orderError)) {
        return;
      }

      console.error(
        "Order creation failed:",
        getSessionExpiredMessage(orderError)
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (value) =>
    `₹${Number(value || 0).toFixed(2)}`;

  return (
    <div className="theme2-order-page">
      <div className="theme2-order-container">

        {/* HEADER */}
        <div className="theme2-order-header">
          <button
            type="button"
            className="theme2-order-back"
            onClick={() => navigate(`${basePath}/checkout`)}
          >
            ← BACK
          </button>

          <div className="theme2-order-header-content">
            <span>FINAL STEP</span>

            <h1>CREATE ORDER</h1>

            <p>
              Review your payment method and place your order.
            </p>
          </div>
        </div>

        {/* ORDER ERROR */}
        {error && (
          <div className="theme2-order-error" role="alert">
            {typeof error === "string"
              ? error
              : "Unable to create order."}
          </div>
        )}

        {/* SESSION EXPIRED */}
        {errorCode === "SESSION_EXPIRED" && (
          <div className="theme2-order-error" role="alert">
            Your table session has ended.
            Please scan the table QR code again to
            start a new session.
          </div>
        )}

        {/* PRICE ERROR */}
        {priceError && (
          <div className="theme2-order-error" role="alert">
            {typeof priceError === "string"
              ? priceError
              : "Unable to calculate the final price."}
          </div>
        )}

        {/* FORM */}
        <form
          className="theme2-order-form"
          onSubmit={handleSubmit}
        >

          {/* PAYMENT METHOD */}
          <section className="theme2-order-section">
            <div className="theme2-order-section-heading">
              <span>01</span>
              <h2>PAYMENT METHOD</h2>
            </div>

            <div className="theme2-order-payment-options">

              {/* CASH */}
              <label
                className={
                  paymentOptions?.allow_cash_payment && paymentMethod === "CASH"
                    ? "theme2-payment-option active"
                    : "theme2-payment-option" + (!paymentOptions?.allow_cash_payment ? " unavailable" : "")
                }
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="CASH"
                  checked={paymentOptions?.allow_cash_payment === true && paymentMethod === "CASH"}
                  disabled={!paymentOptions?.allow_cash_payment}
                  onChange={(event) =>
                    setPaymentMethod(event.target.value)
                  }
                />

                <div className="theme2-payment-option-content">
                  <span className="theme2-payment-option-title">
                    CASH
                  </span>

                  <span className="theme2-payment-option-description">
                    Pay at the restaurant
                  </span>
                </div>

                <span className="theme2-payment-radio" />
              </label>

              {/* ONLINE */}
              <label
                className={
                  paymentOptions?.allow_online_payment && paymentMethod === "ONLINE"
                    ? "theme2-payment-option active"
                    : "theme2-payment-option" + (!paymentOptions?.allow_online_payment ? " unavailable" : "")
                }
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="ONLINE"
                  checked={paymentOptions?.allow_online_payment === true && paymentMethod === "ONLINE"}
                  disabled={!paymentOptions?.allow_online_payment}
                  onChange={(event) =>
                    setPaymentMethod(event.target.value)
                  }
                />

                <div className="theme2-payment-option-content">
                  <span className="theme2-payment-option-title">
                    ONLINE PAYMENT
                  </span>

                  <span className="theme2-payment-option-description">
                    {paymentOptions?.allow_online_payment ? "Pay securely online" : "Online payment unavailable"}
                  </span>
                </div>

                <span className="theme2-payment-radio" />
              </label>
            </div>
          </section>

          {/* SPECIAL INSTRUCTIONS */}
          <section className="theme2-order-section">
            <div className="theme2-order-section-heading">
              <span>02</span>
              <h2>SPECIAL INSTRUCTIONS</h2>
            </div>

            <textarea
              className="theme2-order-notes"
              value={notes}
              onChange={(event) =>
                setNotes(event.target.value)
              }
              placeholder="Any special instructions?"
              rows="4"
            />
          </section>

          {/* ORDER SUMMARY */}
          <section className="theme2-order-summary">
            <div className="theme2-order-summary-label">
              ORDER SUMMARY
            </div>

            <h2>YOUR ORDER</h2>

            {/* ITEMS */}
            <div className="theme2-order-summary-row">
              <span>ITEMS</span>
              <strong>{cartCount}</strong>
            </div>

            {/* SUBTOTAL */}
            <div className="theme2-order-summary-row">
              <span>SUBTOTAL</span>

              <strong>
                {priceLoading
                  ? "Calculating..."
                  : priceSummary
                  ? formatPrice(priceSummary.subtotal)
                  : "—"}
              </strong>
            </div>

            {/* TAX */}
            {priceSummary && (
              <div className="theme2-order-summary-row">
                <span>TAX</span>
                <strong>
                  {formatPrice(priceSummary.tax)}
                </strong>
              </div>
            )}

            {/* SERVICE CHARGE */}
            {priceSummary && (
              <div className="theme2-order-summary-row">
                <span>SERVICE CHARGE</span>
                <strong>
                  {formatPrice(priceSummary.service_charge)}
                </strong>
              </div>
            )}

            {/* DISCOUNT */}
            {priceSummary &&
              Number(priceSummary.discount || 0) > 0 && (
                <div className="theme2-order-summary-row">
                  <span>DISCOUNT</span>
                  <strong>
                    -{formatPrice(priceSummary.discount)}
                  </strong>
                </div>
              )}

            {/* TOTAL */}
            <div className="theme2-order-summary-total">
              <span>TOTAL</span>

              <strong>
                {priceLoading
                  ? "Calculating..."
                  : priceSummary
                  ? formatPrice(priceSummary.total)
                  : "—"}
              </strong>
            </div>

            {/* CREATE ORDER */}
            <button
              type="submit"
              className="theme2-create-order-button"
              disabled={
                loading ||
                submitting ||
                cart.length === 0 ||
                !sessionToken ||
                !finalPriceReady ||
                !paymentOptions ||
                !paymentOptions[`allow_${paymentMethod.toLowerCase()}_payment`]
              }
            >
              {loading || submitting
                ? "CREATING ORDER..."
                : !sessionToken
                ? "DINING SESSION UNAVAILABLE"
                : priceLoading
                ? "CALCULATING PRICE..."
                : "CREATE ORDER →"}
            </button>

            <p className="theme2-order-note">
              {paymentMethod === "CASH"
                ? "You will pay at the restaurant."
                : "You will continue to secure online payment."}
            </p>
          </section>
        </form>
      </div>
    </div>
  );
}

export default OrderScreen;